/**
 * dataLayer push helpers for GTM (APP-2232). Safe to call before `initGtm`
 * has run -- pushes just queue up in `window.dataLayer` and GTM drains them
 * once it loads, which is the intended GTM behaviour, so this deliberately
 * does not add its own buffering on top.
 *
 * Both helpers swallow rather than throw, following the best-effort pattern
 * used for the localStorage helpers in
 * components/auth/cognito/configureAmplify.ts: a broken analytics push must
 * never break the feature that triggered it.
 */
import { AnalyticsEvent } from './events';

/**
 * Pushes a GTM event, e.g.
 * `pushDataLayerEvent({ event: 'msr3_return_to_msr2', reason: 'nav-back' })`.
 * Only accepts events from `AnalyticsEvent` (events.ts) -- reuse an existing
 * event/param name from there rather than inventing a new one. That file's
 * types are half of a cross-repo contract with MSR2 that nothing here can
 * check for you; see the file banner in events.ts before adding an event.
 */
export function pushDataLayerEvent(event: AnalyticsEvent): void {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ...event });
  } catch {
    // best-effort
  }
}

/**
 * Pushes user-scoped properties for GTM variables to read, alongside an
 * `identity_set` event so GTM actually has something to trigger a
 * user-properties tag on -- a `dataLayer.push` with no `event` key updates
 * GTM's data model but fires nothing.
 *
 * `props` is spread BEFORE the `event` key, not after: the signature accepts
 * any `Record<string, unknown>`, so a caller could pass a bag that happens to
 * carry its own `event` -- and spreading last would let it overwrite the event
 * name, leaving a push no tag can trigger on. MSR2's twin
 * (`core/analytics/data-layer.ts`) orders it the same way; keep them in step.
 */
export function setUserProperties(props: Record<string, unknown>): void {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ...props, event: 'identity_set' });
  } catch {
    // best-effort
  }
}
